/* ============================================================
   sticky-offer-bar.js — <sticky-offer-bar> custom element
   - Reveals after a delay if not dismissed in localStorage
   - Hides on cart and checkout pages when configured
   - Click discount code → copy to clipboard with feedback
   ============================================================ */
class StickyOfferBar extends HTMLElement {
  connectedCallback() {
    const key = this.dataset.storage || 'lanuit-offer-dismissed';
    const delay = parseInt(this.dataset.delayMs || '0', 10);
    const hideOnCart = this.dataset.hideOnCart === 'true';
    const isBottom = this.classList.contains('sticky-offer-bar--bottom');

    if (hideOnCart && /^\/cart(\/|$)|^\/checkouts?\//i.test(window.location.pathname)) return;
    if (this.isDismissed(key)) return;

    setTimeout(() => {
      this.hidden = false;
      requestAnimationFrame(() => {
        this.classList.add('is-visible');
        if (isBottom) this.applyBodyPadding();
      });
    }, Math.max(0, delay));

    // Recompute padding on viewport resize (the bar can wrap to 2 lines on
    // mobile, which changes its height).
    if (isBottom) {
      let raf = 0;
      window.addEventListener('resize', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.applyBodyPadding());
      });
    }

    this.querySelector('[data-dismiss]')?.addEventListener('click', () => {
      this.classList.remove('is-visible');
      this.addEventListener('transitionend', () => { this.hidden = true; }, { once: true });
      this.clearBodyPadding();
      try { localStorage.setItem(key, '1'); } catch (_) { /* private mode */ }
    });

    const codeEl = this.querySelector('[data-copy-code]');
    if (codeEl) {
      codeEl.addEventListener('click', async () => {
        const code = codeEl.dataset.code;
        if (!code) return;
        try {
          await navigator.clipboard.writeText(code);
          codeEl.classList.add('is-copied');
          const label = codeEl.querySelector('span > strong');
          if (label) {
            const original = label.textContent;
            label.textContent = 'Copied!';
            setTimeout(() => { label.textContent = original; codeEl.classList.remove('is-copied'); }, 1600);
          }
        } catch (_) { /* clipboard blocked — silent */ }
      });
    }
  }

  isDismissed(key) {
    try { return localStorage.getItem(key) === '1'; }
    catch (_) { return false; }
  }

  /* Measure the bar's rendered height and reserve that much body padding
     so the bar never permanently hides footer / sticky-buy / form content. */
  applyBodyPadding() {
    if (this.hidden) return;
    const h = Math.ceil(this.getBoundingClientRect().height);
    if (h > 0) {
      document.documentElement.style.setProperty('--lanuit-sticky-offer-h', h + 'px');
    }
  }
  clearBodyPadding() {
    document.documentElement.style.removeProperty('--lanuit-sticky-offer-h');
  }
}

if (!customElements.get('sticky-offer-bar')) {
  customElements.define('sticky-offer-bar', StickyOfferBar);
}
